import React, { useEffect, useState } from 'react';
import type {
  AdminQuizCampaign,
  AdminQuizCampaignStats,
  QuizCampaignAudience,
  QuizQuestionAdmin,
  QuizQuestionMedia,
  QuizQuestionMediaKind,
  QuizQuestionMediaSource,
  QuizQuestionKind,
  QuizSession,
  QuizSurveyResults,
} from '../../types';
import { Plus, Trash2, X, ChevronDown, ChevronUp, Pencil, Check, Globe, Archive, AlertTriangle, Layers, Eye, BarChart3 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../../lib/utils';
import { apiFetch } from '../../lib/api';
import { deleteQuizCampaign, deleteQuizRound } from '../../lib/admin-pin-client';
import { pushScoringToast, quizCampaignDeletedToast, roundDeletedToast } from '../common/admin-toasts';
import { usePinDelete } from '../common/PinDeleteDialog';
import { QUIZ_STATIC_ASSET_PREFIX } from '../../lib/quiz-media';
import { useApiJson } from '../../hooks/useApi';
import { useNavigate } from 'react-router-dom';
import { MarkdownField } from '../markdown/MarkdownField';

interface CampaignsListDto {
  ok: boolean;
  campaigns: AdminQuizCampaign[];
}

type AdminQuizSession = QuizSession & {
  questionCount: number;
  scoredQuestionCount: number;
  hasSurveyQuestion: boolean;
  createdAt: string;
  updatedAt: string;
};

interface CampaignDetailDto {
  ok: boolean;
  campaign: AdminQuizCampaign;
  sessions: AdminQuizSession[];
}

interface CampaignStatsDto {
  ok: boolean;
  stats: AdminQuizCampaignStats;
}

type PublishReadiness = {
  ready: boolean;
  issues: string[];
};

interface SessionQuestionsDto {
  ok: boolean;
  hasAttempts: boolean;
  questions: QuizQuestionAdmin[];
}

interface SurveyResultsDto {
  ok: boolean;
  results: QuizSurveyResults;
}

type CampaignFormState = {
  slug: string;
  title: string;
  description: string;
  startsAt: string;
  endsAt: string;
  audience: QuizCampaignAudience;
  roundTimerSeconds: string;
  questionTimerSeconds: string;
  sponsorNames: string;
  sponsorLogoUrl: string;
  sponsorLinkUrl: string;
  sponsorTagline: string;
};

const emptyQuestion = (): QuizQuestionAdmin => ({
  id: 0,
  kind: 'scored',
  question: '',
  options: ['', '', '', ''],
  correctAnswer: 0,
  points: 1,
  position: 0,
  media: null,
});

const defaultForm = (): CampaignFormState => ({
  slug: '',
  title: '',
  description: '',
  startsAt: '',
  endsAt: '',
  audience: 'members_only',
  roundTimerSeconds: '300',
  questionTimerSeconds: '',
  sponsorNames: '',
  sponsorLogoUrl: '',
  sponsorLinkUrl: '',
  sponsorTagline: '',
});

function getPublishReadiness(sessions: AdminQuizSession[]): PublishReadiness {
  const issues: string[] = [];
  if (sessions.length === 0) {
    issues.push('Add at least one round');
  }
  for (const session of sessions) {
    if (session.scoredQuestionCount === 0) {
      issues.push(`Add at least one scored question to "${session.label}"`);
    }
  }
  return { ready: issues.length === 0, issues };
}

function describeCampaignTimer(campaign: AdminQuizCampaign): string {
  if (campaign.timerMode === 'per_question' && campaign.questionTimerSeconds) {
    return `Per question: ${campaign.questionTimerSeconds}s each`;
  }
  return `Per round: ${campaign.roundTimerSeconds}s total`;
}

function toDatetimeLocalValue(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function campaignToForm(c: AdminQuizCampaign): CampaignFormState {
  return {
    slug: c.slug,
    title: c.title,
    description: c.description,
    startsAt: c.startsAt,
    endsAt: c.endsAt,
    audience: c.audience,
    roundTimerSeconds: String(c.roundTimerSeconds),
    questionTimerSeconds: c.questionTimerSeconds ? String(c.questionTimerSeconds) : '',
    sponsorNames: c.sponsorNames.join(' · '),
    sponsorLogoUrl: c.sponsorLogoUrl ?? '',
    sponsorLinkUrl: c.sponsorLinkUrl ?? '',
    sponsorTagline: c.sponsorTagline ?? '',
  };
}

function formToPayload(form: CampaignFormState) {
  return {
    ...form,
    slug: form.slug.trim() || undefined,
    timerSeconds: Number(form.roundTimerSeconds),
    questionTimerSeconds: form.questionTimerSeconds.trim() ? Number(form.questionTimerSeconds) : null,
    sponsorNames: form.sponsorNames.trim() ? [form.sponsorNames.trim()] : [],
    sponsorLogoUrl: form.sponsorLogoUrl || null,
    sponsorLinkUrl: form.sponsorLinkUrl || null,
    sponsorTagline: form.sponsorTagline || null,
  };
}

export function QuizCampaignsManagement() {
  const { data, mutate } = useApiJson<CampaignsListDto>('/api/admin/quiz-campaigns');
  const [removedCampaignIds, setRemovedCampaignIds] = useState<Set<string>>(new Set());
  const pinDelete = usePinDelete();
  const campaigns = (data?.campaigns ?? []).filter((campaign) => !removedCampaignIds.has(campaign.id));
  const navigate = useNavigate();

  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<CampaignDetailDto | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [stats, setStats] = useState<AdminQuizCampaignStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [statsExpandedFor, setStatsExpandedFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [form, setForm] = useState<CampaignFormState>(defaultForm());
  const [editingCampaignId, setEditingCampaignId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<CampaignFormState>(defaultForm());
  const [savingEdit, setSavingEdit] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const [sessionForm, setSessionForm] = useState({
    label: '',
    opensAt: '',
    closesAt: '',
    sortOrder: 0,
  });
  const [sessionFormError, setSessionFormError] = useState<string | null>(null);
  const [addingSession, setAddingSession] = useState(false);
  const [roundAddMode, setRoundAddMode] = useState<'single' | 'bulk'>('bulk');
  const [bulkForm, setBulkForm] = useState({
    count: 7,
    firstOpensAt: '',
    firstClosesAt: '',
    labelPrefix: 'Day',
  });
  const [bulkFormError, setBulkFormError] = useState<string | null>(null);
  const [addingBulk, setAddingBulk] = useState(false);

  const [editSessionModal, setEditSessionModal] = useState<AdminQuizSession | null>(null);
  const [editSessionForm, setEditSessionForm] = useState({
    label: '',
    opensAt: '',
    closesAt: '',
    sortOrder: 0,
  });
  const [editSessionError, setEditSessionError] = useState<string | null>(null);
  const [savingSessionEdit, setSavingSessionEdit] = useState(false);

  const [questionsModal, setQuestionsModal] = useState<{
    sessionId: string;
    sessionLabel: string;
  } | null>(null);
  const [sessionQuestions, setSessionQuestions] = useState<QuizQuestionAdmin[]>([emptyQuestion()]);
  const [questionsLocked, setQuestionsLocked] = useState(false);
  const [questionsSaveState, setQuestionsSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [questionsSaveError, setQuestionsSaveError] = useState<string | null>(null);
  const [surveyResultsModal, setSurveyResultsModal] = useState<{
    sessionLabel: string;
    results: QuizSurveyResults;
  } | null>(null);

  const loadDetail = async (campaignId: string): Promise<CampaignDetailDto | null> => {
    setLoadingDetail(true);
    setError(null);
    try {
      const res = await apiFetch<CampaignDetailDto>(`/api/admin/quiz-campaigns/${encodeURIComponent(campaignId)}`);
      setDetail(res);
      return res;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load campaign');
      return null;
    } finally {
      setLoadingDetail(false);
    }
  };

  const loadStats = async (campaignId: string): Promise<AdminQuizCampaignStats | null> => {
    setLoadingStats(true);
    try {
      const res = await apiFetch<CampaignStatsDto>(
        `/api/admin/quiz-campaigns/${encodeURIComponent(campaignId)}/stats`,
      );
      setStats(res.stats);
      return res.stats;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load stats');
      return null;
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    if (expandedId) {
      setSessionForm({ label: '', opensAt: '', closesAt: '', sortOrder: 0 });
      setSessionFormError(null);
      setBulkFormError(null);
      setEditSessionModal(null);
      void loadDetail(expandedId);
    } else {
      setDetail(null);
      setStats(null);
      setStatsExpandedFor(null);
      setEditingCampaignId(null);
      setSessionFormError(null);
      setBulkFormError(null);
      setEditSessionModal(null);
    }
  }, [expandedId]);

  useEffect(() => {
    if (detail) {
      setSessionForm((prev) => ({ ...prev, sortOrder: detail.sessions.length }));
    }
  }, [detail?.sessions.length]);

  useEffect(() => {
    if (questionsSaveState !== 'saved') return;
    const id = window.setTimeout(() => setQuestionsSaveState('idle'), 2500);
    return () => window.clearTimeout(id);
  }, [questionsSaveState]);

  useEffect(() => {
    if (!successMessage) return;
    const id = window.setTimeout(() => setSuccessMessage(null), 3000);
    return () => window.clearTimeout(id);
  }, [successMessage]);

  const loadSessionQuestions = async (sessionId: string) => {
    const res = await apiFetch<SessionQuestionsDto>(
      `/api/admin/quiz-sessions/${encodeURIComponent(sessionId)}/questions`,
    );
    setQuestionsLocked(res.hasAttempts);
    setSessionQuestions(res.questions.length > 0 ? res.questions : [emptyQuestion()]);
    setQuestionsSaveState('idle');
    setQuestionsSaveError(null);
  };

  const openQuestionsModal = async (session: QuizSession) => {
    setQuestionsModal({ sessionId: session.id, sessionLabel: session.label });
    setQuestionsSaveState('idle');
    setQuestionsSaveError(null);
    await loadSessionQuestions(session.id);
  };

  const closeQuestionsModal = () => {
    setQuestionsModal(null);
    setQuestionsSaveState('idle');
    setQuestionsSaveError(null);
  };

  const openSurveyResultsModal = async (session: QuizSession) => {
    try {
      const res = await apiFetch<SurveyResultsDto>(
        `/api/admin/quiz-sessions/${encodeURIComponent(session.id)}/survey-results`,
      );
      setSurveyResultsModal({
        sessionLabel: session.label,
        results: res.results,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load survey results');
    }
  };

  const toggleStats = async (campaignId: string) => {
    if (statsExpandedFor === campaignId) {
      setStatsExpandedFor(null);
      return;
    }

    setStatsExpandedFor(campaignId);
    if (stats?.campaignId === campaignId) return;
    await loadStats(campaignId);
  };

  const openCampaignPreview = (campaignId: string) => {
    void navigate(`/admin/quiz-preview/${encodeURIComponent(campaignId)}`);
  };

  const openRoundPreview = (campaignId: string, sessionId: string) => {
    void navigate(
      `/admin/quiz-preview/${encodeURIComponent(campaignId)}/${encodeURIComponent(sessionId)}`,
    );
  };

  const handleCreateCampaign = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setError(null);
    try {
      await apiFetch('/api/admin/quiz-campaigns', {
        method: 'POST',
        body: JSON.stringify(formToPayload(form)),
      });
      setShowCreate(false);
      setForm(defaultForm());
      setSuccessMessage('Campaign created');
      await mutate();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create campaign');
    }
  };

  const handleUpdateCampaign = async (campaignId: string) => {
    setError(null);
    setSavingEdit(true);
    try {
      const published = detail?.campaign.published ?? false;
      await apiFetch(`/api/admin/quiz-campaigns/${encodeURIComponent(campaignId)}`, {
        method: 'PUT',
        body: JSON.stringify({ ...formToPayload(editForm), published }),
      });
      setEditingCampaignId(null);
      setSuccessMessage('Campaign updated');
      await loadDetail(campaignId);
      await mutate();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update campaign');
    } finally {
      setSavingEdit(false);
    }
  };

  const handlePublish = async (campaignId: string) => {
    const readiness = detail ? getPublishReadiness(detail.sessions) : { ready: false, issues: [] };
    if (!readiness.ready) {
      setError(`Cannot publish yet: ${readiness.issues.join('; ')}`);
      return;
    }
    const c = detail?.campaign;
    if (!c) return;
    if (
      !confirm(
        'Publish this quiz? Members will see it during the campaign window. Make sure all rounds and questions are final.',
      )
    ) {
      return;
    }
    setError(null);
    setPublishing(true);
    try {
      await apiFetch(`/api/admin/quiz-campaigns/${encodeURIComponent(campaignId)}`, {
        method: 'PUT',
        body: JSON.stringify({ ...formToPayload(campaignToForm(c)), published: true }),
      });
      setSuccessMessage('Campaign published');
      await loadDetail(campaignId);
      await mutate();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to publish campaign');
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async (campaignId: string) => {
    if (
      !confirm(
        'Archive this quiz? It will be unpublished and hidden from members until you publish again.',
      )
    ) {
      return;
    }
    setError(null);
    setPublishing(true);
    try {
      const c = detail?.campaign;
      if (!c) return;
      await apiFetch(`/api/admin/quiz-campaigns/${encodeURIComponent(campaignId)}`, {
        method: 'PUT',
        body: JSON.stringify({ ...formToPayload(campaignToForm(c)), published: false }),
      });
      setSuccessMessage('Campaign archived (unpublished)');
      await loadDetail(campaignId);
      await mutate();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to archive campaign');
    } finally {
      setPublishing(false);
    }
  };

  const handleDeleteCampaign = (id: string, title: string) => {
    pinDelete.ask({
      title: `Delete quiz campaign “${title}”?`,
      description: 'Rounds and questions in this campaign are deleted with it. This cannot be undone.',
      confirmLabel: 'Delete campaign',
      run: async (pin) => {
        await deleteQuizCampaign(id, pin);
        setRemovedCampaignIds((prev) => new Set(prev).add(id));
        if (expandedId === id) setExpandedId(null);
        pushScoringToast(quizCampaignDeletedToast(title));
        void mutate();
      },
    });
  };

  const validateSessionForm = (): string | null => {
    if (!sessionForm.label.trim()) return 'Round label is required';
    if (!sessionForm.opensAt) return 'Opens at is required';
    if (!sessionForm.closesAt) return 'Closes at is required';
    if (Date.parse(sessionForm.opensAt) >= Date.parse(sessionForm.closesAt)) {
      return 'Closes at must be after opens at';
    }
    return null;
  };

  const updateSessionForm = (patch: Partial<typeof sessionForm>) => {
    setSessionFormError(null);
    setSessionForm((prev) => ({ ...prev, ...patch }));
  };

  const handleAddSession = async (campaignId: string) => {
    const validationError = validateSessionForm();
    if (validationError) {
      setSessionFormError(validationError);
      return;
    }

    setSessionFormError(null);
    setError(null);
    setAddingSession(true);
    try {
      await apiFetch(`/api/admin/quiz-campaigns/${encodeURIComponent(campaignId)}/sessions`, {
        method: 'POST',
        body: JSON.stringify(sessionForm),
      });
      const updated = await loadDetail(campaignId);
      if (updated) {
        setSessionForm({
          label: '',
          opensAt: '',
          closesAt: '',
          sortOrder: updated.sessions.length,
        });
      }
      setSuccessMessage('Round added');
      await mutate();
    } catch (e) {
      setSessionFormError(e instanceof Error ? e.message : 'Failed to add round');
    } finally {
      setAddingSession(false);
    }
  };

  const validateBulkForm = (): string | null => {
    if (!Number.isInteger(bulkForm.count) || bulkForm.count < 1 || bulkForm.count > 60) {
      return 'Number of rounds must be between 1 and 60';
    }
    if (!bulkForm.firstOpensAt) return 'First day opens at is required';
    if (!bulkForm.firstClosesAt) return 'First day closes at is required';
    if (Date.parse(bulkForm.firstOpensAt) >= Date.parse(bulkForm.firstClosesAt)) {
      return 'Closes at must be after opens at';
    }
    if (!bulkForm.labelPrefix.trim()) return 'Label prefix is required';
    return null;
  };

  const handleBulkAddSessions = async (campaignId: string) => {
    const validationError = validateBulkForm();
    if (validationError) {
      setBulkFormError(validationError);
      return;
    }

    setBulkFormError(null);
    setError(null);
    setAddingBulk(true);
    try {
      const res = await apiFetch<{ ok: boolean; count: number }>(
        `/api/admin/quiz-campaigns/${encodeURIComponent(campaignId)}/sessions/bulk`,
        {
          method: 'POST',
          body: JSON.stringify({
            count: bulkForm.count,
            firstOpensAt: bulkForm.firstOpensAt,
            firstClosesAt: bulkForm.firstClosesAt,
            labelPrefix: bulkForm.labelPrefix.trim(),
          }),
        },
      );
      await loadDetail(campaignId);
      setSuccessMessage(`${res.count} round${res.count === 1 ? '' : 's'} added`);
      await mutate();
    } catch (e) {
      setBulkFormError(e instanceof Error ? e.message : 'Failed to bulk add rounds');
    } finally {
      setAddingBulk(false);
    }
  };

  const openEditSessionModal = (session: AdminQuizSession) => {
    setEditSessionModal(session);
    setEditSessionForm({
      label: session.label,
      opensAt: session.opensAt,
      closesAt: session.closesAt,
      sortOrder: session.sortOrder,
    });
    setEditSessionError(null);
  };

  const closeEditSessionModal = () => {
    setEditSessionModal(null);
    setEditSessionError(null);
  };

  const handleUpdateSession = async (campaignId: string) => {
    if (!editSessionModal) return;
    if (!editSessionForm.label.trim()) {
      setEditSessionError('Round label is required');
      return;
    }
    if (!editSessionForm.opensAt || !editSessionForm.closesAt) {
      setEditSessionError('Opens and closes times are required');
      return;
    }
    if (Date.parse(editSessionForm.opensAt) >= Date.parse(editSessionForm.closesAt)) {
      setEditSessionError('Closes at must be after opens at');
      return;
    }

    setEditSessionError(null);
    setSavingSessionEdit(true);
    try {
      await apiFetch(`/api/admin/quiz-sessions/${encodeURIComponent(editSessionModal.id)}`, {
        method: 'PUT',
        body: JSON.stringify(editSessionForm),
      });
      closeEditSessionModal();
      setSuccessMessage('Round updated');
      await loadDetail(campaignId);
      await mutate();
    } catch (e) {
      setEditSessionError(e instanceof Error ? e.message : 'Failed to update round');
    } finally {
      setSavingSessionEdit(false);
    }
  };

  const handleDeleteSession = (sessionId: string, campaignId: string, label: string) => {
    pinDelete.ask({
      title: `Delete round “${label}”?`,
      description: 'This cannot be undone.',
      confirmLabel: 'Delete round',
      run: async (pin) => {
        await deleteQuizRound(sessionId, pin);
        setDetail((current) =>
          current && current.campaign.id === campaignId
            ? { ...current, sessions: current.sessions.filter((session) => session.id !== sessionId) }
            : current,
        );
        if (questionsModal?.sessionId === sessionId) closeQuestionsModal();
        pushScoringToast(roundDeletedToast(label));
        void loadDetail(campaignId);
        void mutate();
      },
    });
  };

  const handleSaveQuestions = async (sessionId: string) => {
    setQuestionsSaveState('saving');
    setQuestionsSaveError(null);
    try {
      await apiFetch(`/api/admin/quiz-sessions/${encodeURIComponent(sessionId)}/questions`, {
        method: 'PUT',
        body: JSON.stringify({
          questions: sessionQuestions.map(({ kind, question, options, correctAnswer, points, media }) => ({
            kind,
            question,
            options,
            correctAnswer: kind === 'survey' ? undefined : correctAnswer,
            points: kind === 'survey' ? undefined : points,
            media,
          })),
        }),
      });
      await loadSessionQuestions(sessionId);
      setQuestionsSaveState('saved');
      if (expandedId) await loadDetail(expandedId);
    } catch (e) {
      setQuestionsSaveState('error');
      setQuestionsSaveError(e instanceof Error ? e.message : 'Failed to save questions');
    }
  };

  const updateQuestion = (idx: number, field: keyof QuizQuestionAdmin, val: unknown) => {
    const updated = [...sessionQuestions];
    updated[idx] = { ...updated[idx]!, [field]: val } as QuizQuestionAdmin;
    setSessionQuestions(updated);
    if (questionsSaveState === 'saved') setQuestionsSaveState('idle');
  };

  const updateOption = (qIdx: number, oIdx: number, val: string) => {
    const updated = [...sessionQuestions];
    const opts = [...updated[qIdx]!.options];
    opts[oIdx] = val;
    updated[qIdx] = { ...updated[qIdx]!, options: opts };
    setSessionQuestions(updated);
    if (questionsSaveState === 'saved') setQuestionsSaveState('idle');
  };

  const setQuestionKind = (qIdx: number, kind: QuizQuestionKind) => {
    const hasExistingSurvey = sessionQuestions.some((q, idx) => idx !== qIdx && q.kind === 'survey');
    if (kind === 'survey' && hasExistingSurvey) {
      setQuestionsSaveError('Only one survey question is allowed per round');
      setQuestionsSaveState('error');
      return;
    }

    const updated = [...sessionQuestions];
    updated[qIdx] = {
      ...updated[qIdx]!,
      kind,
      correctAnswer: kind === 'survey' ? 0 : updated[qIdx]!.correctAnswer,
      points: kind === 'survey' ? 0 : Math.max(1, updated[qIdx]!.points || 1),
    };
    setSessionQuestions(updated);
    if (questionsSaveState === 'saved') setQuestionsSaveState('idle');
    if (questionsSaveState === 'error') {
      setQuestionsSaveState('idle');
      setQuestionsSaveError(null);
    }
  };

  const startEditCampaign = (c: AdminQuizCampaign) => {
    setEditForm(campaignToForm(c));
    setEditingCampaignId(c.id);
  };

  return (
    <div className="space-y-4">
      {pinDelete.dialog}
      {error && (
        <p className="text-sm text-[var(--color-error)] bg-[var(--color-error-subtle)] px-4 py-2 rounded-[var(--radius-md)]">
          {error}
        </p>
      )}
      {successMessage && (
        <p className="text-sm text-[var(--color-success)] bg-[var(--color-success-subtle)] px-4 py-2 rounded-[var(--radius-md)]">
          {successMessage}
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setShowCreate(!showCreate)}
          className={cn(
            'flex items-center gap-2 px-4 py-2.5 rounded-[var(--radius-md)] font-medium text-sm transition-colors duration-[var(--duration-fast)]',
            showCreate
              ? 'bg-[var(--color-surface-2)] text-[var(--color-text-secondary)]'
              : 'bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)]',
          )}
        >
          {showCreate ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showCreate ? 'Cancel' : 'New Campaign'}
        </button>
      </div>

      <AnimatePresence>
        {showCreate && (
          <motion.form
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            onSubmit={handleCreateCampaign}
            className="overflow-hidden bg-[var(--color-surface-1)] p-6 rounded-[var(--radius-lg)] border border-[var(--color-border)] space-y-4"
          >
            <CampaignFields form={form} setForm={setForm} />
            <button type="submit" className="bg-[var(--color-accent)] text-white px-4 py-2.5 rounded-[var(--radius-md)] text-sm font-medium">
              Create draft campaign
            </button>
          </motion.form>
        )}
      </AnimatePresence>

      <div className="space-y-3">
        {campaigns.map((campaign) => {
          const expanded = expandedId === campaign.id;
          const isEditing = editingCampaignId === campaign.id;
          return (
            <div key={campaign.id} className="bg-[var(--color-surface-1)] rounded-[var(--radius-lg)] border border-[var(--color-border)] overflow-hidden">
              <div className="p-4 flex justify-between items-start gap-4">
                <button
                  type="button"
                  className="flex-1 text-left"
                  onClick={() => setExpandedId(expanded ? null : campaign.id)}
                >
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">{campaign.title}</h3>
                    {campaign.published ? (
                      <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-[var(--color-accent-subtle)] text-[var(--color-accent)]">
                        Published
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-[var(--color-surface-2)] text-[var(--color-text-tertiary)]">
                        Draft
                      </span>
                    )}
                    {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                  <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">
                    {campaign.slug} · {campaign.sessionCount} round{campaign.sessionCount === 1 ? '' : 's'} · {campaign.audience === 'members_only' ? 'Members only' : 'All signed-in'}
                  </p>
                </button>
                <div className="flex gap-1">
                  {expanded && detail?.campaign.id === campaign.id && !isEditing && (
                    <button
                      type="button"
                      onClick={() => startEditCampaign(detail.campaign)}
                      className="p-2 hover:bg-[var(--color-surface-2)] text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] rounded-[var(--radius-sm)]"
                      title="Edit campaign"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                  {!campaign.hasAttempts && (
                    <button
                      type="button"
                      onClick={() => handleDeleteCampaign(campaign.id, campaign.title)}
                      className="p-2 hover:bg-[var(--color-error-subtle)] text-[var(--color-text-tertiary)] hover:text-[var(--color-error)] rounded-[var(--radius-sm)]"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {expanded && (
                <div className="border-t border-[var(--color-border)] p-4 space-y-6">
                  {loadingDetail && <p className="text-sm text-[var(--color-text-secondary)]">Loading…</p>}
                  {detail?.campaign.id === campaign.id && (
                    <>
                      {isEditing ? (
                        <div className="space-y-4">
                          <CampaignFields form={editForm} setForm={setEditForm} />
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              disabled={savingEdit}
                              onClick={() => void handleUpdateCampaign(campaign.id)}
                              className="bg-[var(--color-accent)] text-white px-4 py-2 rounded-[var(--radius-md)] text-sm font-medium disabled:opacity-60"
                            >
                              {savingEdit ? 'Saving…' : 'Save changes'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingCampaignId(null)}
                              className="bg-[var(--color-surface-2)] px-4 py-2 rounded-[var(--radius-md)] text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <p><span className="text-[var(--color-text-tertiary)]">Window:</span> {new Date(detail.campaign.startsAt).toLocaleString()} – {new Date(detail.campaign.endsAt).toLocaleString()}</p>
                            <p>
                              <span className="text-[var(--color-text-tertiary)]">Effective timer:</span>{' '}
                              {describeCampaignTimer(detail.campaign)}
                            </p>
                            <p>
                              <span className="text-[var(--color-text-tertiary)]">Round timer fallback:</span>{' '}
                              {detail.campaign.roundTimerSeconds}s ({Math.round(detail.campaign.roundTimerSeconds / 60)} min)
                              {detail.campaign.timerMode === 'per_question' && detail.campaign.questionTimerSeconds ? ' · ignored while question timer is set' : ''}
                            </p>
                            {detail.campaign.sponsorNames.length > 0 && (
                              <p className="md:col-span-2">
                                <span className="text-[var(--color-text-tertiary)]">Sponsor:</span>{' '}
                                {detail.campaign.sponsorNames.join(' · ')}
                                {detail.campaign.sponsorLinkUrl && (
                                  <span className="text-[var(--color-text-tertiary)]">
                                    {' '}
                                    (
                                    <a
                                      href={detail.campaign.sponsorLinkUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-[var(--color-accent)] hover:underline"
                                    >
                                      link
                                    </a>
                                    )
                                  </span>
                                )}
                              </p>
                            )}
                          </div>

                          {(() => {
                            const readiness = getPublishReadiness(detail.sessions);
                            const isPublished = detail.campaign.published;
                            return (
                              <div
                                className={cn(
                                  'rounded-[var(--radius-lg)] border p-4 space-y-3',
                                  isPublished
                                    ? 'border-[var(--color-accent)]/30 bg-[var(--color-accent-subtle)]/40'
                                    : 'border-[var(--color-border)] bg-[var(--color-surface-2)]/50',
                                )}
                              >
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                                  <div className="space-y-2 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <h4 className="text-sm font-semibold text-[var(--color-text-primary)]">
                                        {isPublished ? 'Published' : 'Draft — configure before publishing'}
                                      </h4>
                                      {isPublished ? (
                                        <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-[var(--color-accent-subtle)] text-[var(--color-accent)]">
                                          Live
                                        </span>
                                      ) : (
                                        <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-[var(--color-surface-2)] text-[var(--color-text-tertiary)]">
                                          Not visible to members
                                        </span>
                                      )}
                                    </div>
                                    {!isPublished && (
                                      <>
                                        <p className="text-xs text-[var(--color-text-secondary)] leading-relaxed">
                                          Set up campaign details, add rounds, and add questions to each round — then publish when ready.
                                        </p>
                                        <ul className="text-xs space-y-1">
                                          <li className={cn(
                                            'flex items-center gap-1.5',
                                            detail.sessions.length > 0
                                              ? 'text-[var(--color-success)]'
                                              : 'text-[var(--color-text-tertiary)]',
                                          )}>
                                            {detail.sessions.length > 0 ? <Check className="w-3.5 h-3.5 shrink-0" /> : <span className="w-3.5 h-3.5 shrink-0 rounded-full border border-[var(--color-border)]" />}
                                            At least one round
                                          </li>
                                          {detail.sessions.map((session) => (
                                            <li
                                              key={session.id}
                                              className={cn(
                                                'flex items-center gap-1.5',
                                                session.scoredQuestionCount > 0
                                                  ? 'text-[var(--color-success)]'
                                                  : 'text-[var(--color-text-tertiary)]',
                                              )}
                                            >
                                              {session.scoredQuestionCount > 0 ? (
                                                <Check className="w-3.5 h-3.5 shrink-0" />
                                              ) : (
                                                <span className="w-3.5 h-3.5 shrink-0 rounded-full border border-[var(--color-border)]" />
                                              )}
                                              Scored questions in &ldquo;{session.label}&rdquo;
                                            </li>
                                          ))}
                                        </ul>
                                        {!readiness.ready && readiness.issues.length > 0 && (
                                          <p className="text-xs text-[var(--color-text-tertiary)]">
                                            {readiness.issues.join(' · ')}
                                          </p>
                                        )}
                                      </>
                                    )}
                                    {isPublished && (
                                      <p className="text-xs text-[var(--color-text-secondary)]">
                                        Members can see this quiz during its window. Unpublish to archive and hide it.
                                      </p>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap gap-2 sm:shrink-0">
                                    <button
                                      type="button"
                                      disabled={detail.sessions.length === 0}
                                      title={detail.sessions.length > 0 ? 'Preview all rounds as an admin' : 'Add at least one round to preview this campaign'}
                                      onClick={() => openCampaignPreview(campaign.id)}
                                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[var(--radius-md)] text-sm font-medium border border-[var(--color-border)] bg-[var(--color-surface-1)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-2)] disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <Eye className="w-4 h-4" />
                                      Preview campaign
                                    </button>
                                    {isPublished ? (
                                      <button
                                        type="button"
                                        disabled={publishing}
                                        onClick={() => void handleUnpublish(campaign.id)}
                                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[var(--radius-md)] text-sm font-medium border border-[var(--color-border)] bg-[var(--color-surface-1)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-2)] disabled:opacity-60"
                                      >
                                        <Archive className="w-4 h-4" />
                                        {publishing ? 'Archiving…' : 'Unpublish'}
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        disabled={publishing || !readiness.ready}
                                        title={readiness.ready ? 'Publish to members' : readiness.issues.join('; ')}
                                        onClick={() => void handlePublish(campaign.id)}
                                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[var(--radius-md)] text-sm font-medium bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        <Globe className="w-4 h-4" />
                                        {publishing ? 'Publishing…' : 'Publish'}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}

                          <p className="text-xs text-[var(--color-text-tertiary)] leading-relaxed">
                            Each <strong className="font-medium text-[var(--color-text-secondary)]">round</strong> is a session with its own open/close window and questions.
                            If a per-question timer is set, it overrides the round timer. Otherwise the round timer applies to the full attempt.
                          </p>
                        </div>
                      )}

                      <div className="space-y-4">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <h4 className="text-sm font-semibold text-[var(--color-text-primary)]">Stats</h4>
                            <p className="text-xs text-[var(--color-text-tertiary)] mt-0.5">
                              Participation, round completion, and leaderboard snapshot for this campaign.
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            {loadingStats && statsExpandedFor === campaign.id && (
                              <p className="text-xs text-[var(--color-text-secondary)]">Refreshing…</p>
                            )}
                            <button
                              type="button"
                              onClick={() => void toggleStats(campaign.id)}
                              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-1)] text-xs font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-2)]"
                            >
                              {statsExpandedFor === campaign.id ? 'Hide stats' : 'Show stats'}
                            </button>
                          </div>
                        </div>
                        {statsExpandedFor === campaign.id && stats?.campaignId === campaign.id && (
                          <>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              <StatCard label="Participants" value={`${stats.totalParticipants}`} meta={`${stats.totalEligibleParticipants} eligible`} />
                              <StatCard label="Participation" value={`${stats.participationPct}%`} meta={stats.audience === 'members_only' ? 'members + admins' : 'all signed-in users'} />
                              <StatCard label="Attempts" value={`${stats.totalAttempts}`} meta={`${stats.averageRoundsCompleted} rounds per player`} />
                              <StatCard label="Average Score" value={`${stats.averageScorePct}%`} meta={`${Math.round(stats.averageDurationSeconds)}s avg duration`} />
                            </div>

                            <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-4">
                              <div className="border border-[var(--color-border)] rounded-[var(--radius-lg)] overflow-hidden">
                                <div className="px-4 py-3 border-b border-[var(--color-border)] bg-[var(--color-surface-2)]">
                                  <h5 className="text-sm font-semibold text-[var(--color-text-primary)]">Round breakdown</h5>
                                </div>
                                <div className="divide-y divide-[var(--color-border)]">
                                  {stats.sessionStats.length === 0 ? (
                                    <p className="px-4 py-3 text-sm text-[var(--color-text-secondary)]">No rounds yet.</p>
                                  ) : (
                                    stats.sessionStats.map((session) => (
                                      <div key={session.sessionId} className="px-4 py-3 space-y-2">
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                          <div>
                                            <p className="text-sm font-medium text-[var(--color-text-primary)]">{session.label}</p>
                                            <p className="text-xs text-[var(--color-text-tertiary)]">
                                              {session.uniqueParticipants} participants · {session.submissions} submissions
                                            </p>
                                          </div>
                                          <div className="text-right">
                                            <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                                              {session.avgScorePct}%
                                            </p>
                                            <p className="text-xs text-[var(--color-text-tertiary)]">
                                              avg {session.avgScore}/{session.maxPossibleScore}
                                            </p>
                                          </div>
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-[var(--color-text-secondary)]">
                                          <p>Completion: {session.completionPct}%</p>
                                          <p>Top score: {session.topScore}/{session.maxPossibleScore}</p>
                                          <p>Avg duration: {Math.round(session.avgDurationSeconds)}s</p>
                                          <p>
                                            Last submit:{' '}
                                            {session.lastSubmittedAt
                                              ? new Date(session.lastSubmittedAt).toLocaleTimeString()
                                              : 'None'}
                                          </p>
                                        </div>
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>

                              <div className="border border-[var(--color-border)] rounded-[var(--radius-lg)] overflow-hidden">
                                <div className="px-4 py-3 border-b border-[var(--color-border)] bg-[var(--color-surface-2)]">
                                  <h5 className="text-sm font-semibold text-[var(--color-text-primary)]">Top performers</h5>
                                </div>
                                <div className="divide-y divide-[var(--color-border)]">
                                  {stats.topPerformers.length === 0 ? (
                                    <p className="px-4 py-3 text-sm text-[var(--color-text-secondary)]">No submissions yet.</p>
                                  ) : (
                                    stats.topPerformers.map((entry) => (
                                      <div key={entry.uid} className="px-4 py-3 flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                          <p className="text-sm font-medium text-[var(--color-text-primary)]">
                                            #{entry.rank} {entry.name}
                                          </p>
                                          <p className="text-xs text-[var(--color-text-tertiary)]">
                                            {entry.roundsCompleted} rounds · {entry.scorePct}% overall
                                          </p>
                                        </div>
                                        <div className="text-right shrink-0">
                                          <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                                            {entry.totalPoints}/{entry.maxPoints}
                                          </p>
                                          <p className="text-xs text-[var(--color-text-tertiary)]">
                                            {entry.totalDurationSeconds}s
                                          </p>
                                        </div>
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                        {statsExpandedFor !== campaign.id && (
                          <p className="text-sm text-[var(--color-text-secondary)] bg-[var(--color-surface-2)] rounded-[var(--radius-md)] px-4 py-3">
                            Stats are collapsed by default.
                          </p>
                        )}
                      </div>

                      <div className="space-y-3 pb-20 md:pb-0">
                        <div>
                          <h4 className="text-sm font-semibold">Rounds</h4>
                          <p className="text-xs text-[var(--color-text-tertiary)] mt-0.5">
                            Add rounds one at a time or bulk-create daily rounds with auto labels (Day 1, Day 2…).
                          </p>
                          {detail.sessions.some((s) => s.scoredQuestionCount === 0) && (
                            <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[var(--color-error)] bg-[var(--color-error-subtle)] px-2.5 py-1.5 rounded-[var(--radius-md)]">
                              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                              {detail.sessions.filter((s) => s.scoredQuestionCount === 0).length} round
                              {detail.sessions.filter((s) => s.scoredQuestionCount === 0).length === 1 ? '' : 's'} still need
                              scored questions before you can publish
                            </p>
                          )}
                        </div>
                        {detail.sessions.map((session) => {
                          const missingQuestions = session.scoredQuestionCount === 0;
                          return (
                          <div
                            key={session.id}
                            className={cn(
                              'border rounded-[var(--radius-md)] p-3',
                              missingQuestions
                                ? 'border-[var(--color-error)]/40 bg-[var(--color-error-subtle)]/30'
                                : 'border-[var(--color-border)]',
                            )}
                          >
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="font-medium text-sm">{session.label}</p>
                                  {missingQuestions ? (
                                    <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-[var(--color-error-subtle)] text-[var(--color-error)]">
                                      <AlertTriangle className="w-3 h-3" />
                                      Needs questions
                                    </span>
                                  ) : (
                                    <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-[var(--color-success-subtle)] text-[var(--color-success)]">
                                      {session.scoredQuestionCount} scored
                                      {session.hasSurveyQuestion ? ' + survey' : ''}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">
                                  {new Date(session.opensAt).toLocaleString()} – {new Date(session.closesAt).toLocaleString()}
                                  {' · '}Order {session.sortOrder}
                                </p>
                              </div>
                              <div className="flex flex-wrap gap-1.5 justify-start sm:shrink-0 sm:justify-end">
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-[var(--color-surface-1)] hover:bg-[var(--color-surface-2)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                                  onClick={() => openEditSessionModal(session)}
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                  Edit round
                                </button>
                                <button
                                  type="button"
                                  disabled={missingQuestions}
                                  title={missingQuestions ? 'Add questions before previewing this round' : 'Preview this round as an admin'}
                                  className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-[var(--color-surface-1)] hover:bg-[var(--color-surface-2)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] disabled:opacity-50 disabled:cursor-not-allowed"
                                  onClick={() => openRoundPreview(campaign.id, session.id)}
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                  Preview
                                </button>
                                <button
                                  type="button"
                                  className={cn(
                                    'inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-[var(--radius-sm)]',
                                    missingQuestions
                                      ? 'bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)]'
                                      : 'text-[var(--color-accent)] hover:bg-[var(--color-accent-subtle)]',
                                  )}
                                  onClick={() => void openQuestionsModal(session)}
                                >
                                  {missingQuestions ? 'Add questions' : 'Edit questions'}
                                </button>
                                <button
                                  type="button"
                                  disabled={!session.hasSurveyQuestion}
                                  title={session.hasSurveyQuestion ? 'View survey responses for this round' : 'Add a survey question to this round first'}
                                  className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-[var(--color-surface-1)] hover:bg-[var(--color-surface-2)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                                  onClick={() => void openSurveyResultsModal(session)}
                                >
                                  <BarChart3 className="w-3.5 h-3.5" />
                                  Survey results
                                </button>
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-[var(--radius-sm)] text-[var(--color-error)] hover:bg-[var(--color-error-subtle)]"
                                  onClick={() => handleDeleteSession(session.id, campaign.id, session.label)}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                        })}

                        <div className="border border-[var(--color-border)] rounded-[var(--radius-lg)] p-4 space-y-4 bg-[var(--color-surface-2)]/30">
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => setRoundAddMode('bulk')}
                              className={cn(
                                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[var(--radius-md)] text-xs font-medium transition-colors',
                                roundAddMode === 'bulk'
                                  ? 'bg-[var(--color-accent)] text-white'
                                  : 'bg-[var(--color-surface-1)] border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
                              )}
                            >
                              <Layers className="w-3.5 h-3.5" />
                              Bulk add rounds
                            </button>
                            <button
                              type="button"
                              onClick={() => setRoundAddMode('single')}
                              className={cn(
                                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[var(--radius-md)] text-xs font-medium transition-colors',
                                roundAddMode === 'single'
                                  ? 'bg-[var(--color-accent)] text-white'
                                  : 'bg-[var(--color-surface-1)] border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
                              )}
                            >
                              <Plus className="w-3.5 h-3.5" />
                              Add single round
                            </button>
                          </div>

                          {roundAddMode === 'bulk' ? (
                            <>
                              <p className="text-xs text-[var(--color-text-tertiary)]">
                                Creates {bulkForm.count} round{bulkForm.count === 1 ? '' : 's'} with daily windows.
                                Each round opens and closes at the same time of day, shifted by one day.
                                Labels are generated as {bulkForm.labelPrefix.trim() || 'Day'} 1, {bulkForm.labelPrefix.trim() || 'Day'} 2, etc.
                              </p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Field
                                  label="Number of rounds"
                                  type="number"
                                  value={String(bulkForm.count)}
                                  onChange={(v) => {
                                    setBulkFormError(null);
                                    setBulkForm({ ...bulkForm, count: Number(v) });
                                  }}
                                  hint="How many daily rounds to create (max 60)"
                                  required
                                />
                                <Field
                                  label="Label prefix"
                                  value={bulkForm.labelPrefix}
                                  onChange={(v) => {
                                    setBulkFormError(null);
                                    setBulkForm({ ...bulkForm, labelPrefix: v });
                                  }}
                                  placeholder="Day"
                                  hint='Round labels become "Day 1", "Day 2", …'
                                  required
                                />
                                <Field
                                  label="Day 1 opens at"
                                  type="datetime-local"
                                  value={toDatetimeLocalValue(bulkForm.firstOpensAt)}
                                  onChange={(v) => {
                                    setBulkFormError(null);
                                    setBulkForm({ ...bulkForm, firstOpensAt: v ? new Date(v).toISOString() : '' });
                                  }}
                                  required
                                />
                                <Field
                                  label="Day 1 closes at"
                                  type="datetime-local"
                                  value={toDatetimeLocalValue(bulkForm.firstClosesAt)}
                                  onChange={(v) => {
                                    setBulkFormError(null);
                                    setBulkForm({ ...bulkForm, firstClosesAt: v ? new Date(v).toISOString() : '' });
                                  }}
                                  required
                                />
                              </div>
                              {bulkFormError && (
                                <p className="text-sm text-[var(--color-error)] bg-[var(--color-error-subtle)] px-3 py-2 rounded-[var(--radius-md)]">
                                  {bulkFormError}
                                </p>
                              )}
                              <button
                                type="button"
                                disabled={addingBulk}
                                onClick={() => void handleBulkAddSessions(campaign.id)}
                                className="text-sm bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-white px-4 py-2.5 rounded-[var(--radius-md)] font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                              >
                                {addingBulk ? 'Creating rounds…' : `Create ${bulkForm.count} round${bulkForm.count === 1 ? '' : 's'}`}
                              </button>
                            </>
                          ) : (
                            <>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Field label="Round label" value={sessionForm.label} onChange={(v) => updateSessionForm({ label: v })} placeholder="Day 1, Round 2…" required />
                                <Field
                                  label="Sort order"
                                  type="number"
                                  value={String(sessionForm.sortOrder)}
                                  onChange={(v) => updateSessionForm({ sortOrder: Number(v) })}
                                  hint="Round sequence (0 = first)"
                                />
                                <Field label="Opens at" type="datetime-local" value={toDatetimeLocalValue(sessionForm.opensAt)} onChange={(v) => updateSessionForm({ opensAt: v ? new Date(v).toISOString() : '' })} required />
                                <Field label="Closes at" type="datetime-local" value={toDatetimeLocalValue(sessionForm.closesAt)} onChange={(v) => updateSessionForm({ closesAt: v ? new Date(v).toISOString() : '' })} required />
                              </div>
                              {sessionFormError && (
                                <p className="text-sm text-[var(--color-error)] bg-[var(--color-error-subtle)] px-3 py-2 rounded-[var(--radius-md)]">
                                  {sessionFormError}
                                </p>
                              )}
                              <button
                                type="button"
                                disabled={addingSession}
                                onClick={() => void handleAddSession(campaign.id)}
                                className="text-sm bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-white px-4 py-2.5 rounded-[var(--radius-md)] font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                              >
                                {addingSession ? 'Adding…' : 'Add round'}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <AnimatePresence>
        {editSessionModal && expandedId && (
          <EditRoundModal
            sessionLabel={editSessionModal.label}
            form={editSessionForm}
            error={editSessionError}
            saving={savingSessionEdit}
            onClose={closeEditSessionModal}
            onChange={(patch) => {
              setEditSessionError(null);
              setEditSessionForm((prev) => ({ ...prev, ...patch }));
            }}
            onSave={() => void handleUpdateSession(expandedId)}
          />
        )}
        {questionsModal && (
          <QuestionsEditorModal
            sessionLabel={questionsModal.sessionLabel}
            questions={sessionQuestions}
            locked={questionsLocked}
            saveState={questionsSaveState}
            saveError={questionsSaveError}
            onClose={closeQuestionsModal}
            onSave={() => void handleSaveQuestions(questionsModal.sessionId)}
            onAddQuestion={() => setSessionQuestions([...sessionQuestions, emptyQuestion()])}
            onRemoveQuestion={(idx) => setSessionQuestions(sessionQuestions.filter((_, i) => i !== idx))}
            onSetQuestionKind={setQuestionKind}
            onUpdateQuestion={updateQuestion}
            onUpdateOption={updateOption}
          />
        )}
        {surveyResultsModal && (
          <SurveyResultsModal
            sessionLabel={surveyResultsModal.sessionLabel}
            results={surveyResultsModal.results}
            onClose={() => setSurveyResultsModal(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function CampaignFields({
  form,
  setForm,
}: {
  form: CampaignFormState;
  setForm: React.Dispatch<React.SetStateAction<CampaignFormState>>;
}) {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required />
        <Field
          label="Slug (optional)"
          value={form.slug}
          onChange={(v) => setForm({ ...form, slug: v })}
          placeholder="Auto-generated from title if empty"
          hint="URL-friendly id — leave blank to auto-generate"
        />
        <Field label="Starts at" type="datetime-local" value={toDatetimeLocalValue(form.startsAt)} onChange={(v) => setForm({ ...form, startsAt: v ? new Date(v).toISOString() : '' })} required />
        <Field label="Ends at" type="datetime-local" value={toDatetimeLocalValue(form.endsAt)} onChange={(v) => setForm({ ...form, endsAt: v ? new Date(v).toISOString() : '' })} required />
        <Field
          label="Round timer fallback (seconds)"
          type="number"
          value={form.roundTimerSeconds}
          onChange={(v) => setForm({ ...form, roundTimerSeconds: v })}
          required
          hint="Used only when question timer is empty. Minimum 30 seconds."
        />
        <Field
          label="Question timer (seconds)"
          type="number"
          value={form.questionTimerSeconds}
          onChange={(v) => setForm({ ...form, questionTimerSeconds: v })}
          hint="Optional. When set, each question gets this timer and the round timer is ignored. Minimum 10 seconds."
        />
        <div className="md:col-span-2 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-2)] px-4 py-3 text-xs text-[var(--color-text-secondary)]">
          Effective mode: {form.questionTimerSeconds.trim() ? `Per question (${form.questionTimerSeconds}s each)` : `Per round (${form.roundTimerSeconds || '0'}s total)`}
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5">Audience</label>
          <select
            className="w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-3 px-4 text-sm"
            value={form.audience}
            onChange={(ev) => setForm({ ...form, audience: ev.target.value as QuizCampaignAudience })}
          >
            <option value="members_only">Members only</option>
            <option value="all_signed_in">All signed-in users</option>
          </select>
        </div>
      </div>
      <MarkdownField
        label="Description"
        value={form.description}
        onChange={(v) => setForm({ ...form, description: v })}
        rows={3}
        hint="Markdown supported. Line breaks are kept on the dashboard, quiz, and leaderboard."
      />
      <Field
        label="Sponsor text"
        value={form.sponsorNames}
        onChange={(v) => setForm({ ...form, sponsorNames: v })}
        placeholder="e.g. Kanta J Narayan Rathi"
        hint="Shown as “Sponsored by …” under the quiz title on dashboard, quiz, and leaderboard"
      />
      <Field
        label="Sponsor link URL"
        value={form.sponsorLinkUrl}
        onChange={(v) => setForm({ ...form, sponsorLinkUrl: v })}
        placeholder="/sponsors"
        hint="Optional — defaults to /sponsors in the app. Use a full URL for an external sponsors page."
      />
      <Field
        label="Sponsor logo URL"
        value={form.sponsorLogoUrl}
        onChange={(v) => setForm({ ...form, sponsorLogoUrl: v })}
        placeholder="har-ghar-bulandi.png"
        hint={
          <>
            Optional image in the quiz header. Use a filename from{' '}
            <code className="font-mono">public/assets/quiz/</code> (e.g.{' '}
            <code className="font-mono">har-ghar-bulandi.png</code>), a path like{' '}
            <code className="font-mono">{QUIZ_STATIC_ASSET_PREFIX}har-ghar-bulandi.png</code>, or a full URL.
          </>
        }
      />
      <Field label="Sponsor tagline" value={form.sponsorTagline} onChange={(v) => setForm({ ...form, sponsorTagline: v })} />
      <p className="text-xs text-[var(--color-text-tertiary)]">
        New campaigns start as drafts. Add rounds and questions after creating, then publish when ready.
      </p>
    </>
  );
}

function EditRoundModal({
  sessionLabel,
  form,
  error,
  saving,
  onClose,
  onChange,
  onSave,
}: {
  sessionLabel: string;
  form: { label: string; opensAt: string; closesAt: string; sortOrder: number };
  error: string | null;
  saving: boolean;
  onClose: () => void;
  onChange: (patch: Partial<{ label: string; opensAt: string; closesAt: string; sortOrder: number }>) => void;
  onSave: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
    >
      <button type="button" className="absolute inset-0 bg-black/40" aria-label="Close" onClick={onClose} />
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        className="relative w-full sm:max-w-lg bg-[var(--color-surface-1)] rounded-t-[var(--radius-xl)] sm:rounded-[var(--radius-xl)] border border-[var(--color-border)] shadow-[var(--shadow-lg)]"
      >
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-[var(--color-border)]">
          <div>
            <h3 className="text-base font-semibold text-[var(--color-text-primary)]">Edit Round</h3>
            <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">{sessionLabel}</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-[var(--radius-sm)] hover:bg-[var(--color-surface-2)]">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <Field label="Round label" value={form.label} onChange={(v) => onChange({ label: v })} required />
          <Field
            label="Sort order"
            type="number"
            value={String(form.sortOrder)}
            onChange={(v) => onChange({ sortOrder: Number(v) })}
            hint="Round sequence (0 = first)"
          />
          <Field
            label="Opens at"
            type="datetime-local"
            value={toDatetimeLocalValue(form.opensAt)}
            onChange={(v) => onChange({ opensAt: v ? new Date(v).toISOString() : '' })}
            required
          />
          <Field
            label="Closes at"
            type="datetime-local"
            value={toDatetimeLocalValue(form.closesAt)}
            onChange={(v) => onChange({ closesAt: v ? new Date(v).toISOString() : '' })}
            required
          />
          {error && (
            <p className="text-sm text-[var(--color-error)] bg-[var(--color-error-subtle)] px-3 py-2 rounded-[var(--radius-md)]">
              {error}
            </p>
          )}
        </div>
        <div className="px-6 py-4 border-t border-[var(--color-border)] flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-[var(--radius-md)] text-sm bg-[var(--color-surface-2)]">
            Cancel
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={onSave}
            className="px-4 py-2 rounded-[var(--radius-md)] text-sm font-medium bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)] disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save round'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function QuestionsEditorModal({
  sessionLabel,
  questions,
  locked,
  saveState,
  saveError,
  onClose,
  onSave,
  onAddQuestion,
  onRemoveQuestion,
  onSetQuestionKind,
  onUpdateQuestion,
  onUpdateOption,
}: {
  sessionLabel: string;
  questions: QuizQuestionAdmin[];
  locked: boolean;
  saveState: 'idle' | 'saving' | 'saved' | 'error';
  saveError: string | null;
  onClose: () => void;
  onSave: () => void;
  onAddQuestion: () => void;
  onRemoveQuestion: (idx: number) => void;
  onSetQuestionKind: (idx: number, kind: QuizQuestionKind) => void;
  onUpdateQuestion: (idx: number, field: keyof QuizQuestionAdmin, val: unknown) => void;
  onUpdateOption: (qIdx: number, oIdx: number, val: string) => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close"
        onClick={onClose}
      />
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        className="relative w-full sm:max-w-3xl max-h-[90vh] overflow-hidden bg-[var(--color-surface-1)] rounded-t-[var(--radius-xl)] sm:rounded-[var(--radius-xl)] border border-[var(--color-border)] shadow-[var(--shadow-lg)] flex flex-col"
      >
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-[var(--color-border)] shrink-0">
          <div>
            <h3 className="text-base font-semibold text-[var(--color-text-primary)]">Edit Questions</h3>
            <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">{sessionLabel}</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-[var(--radius-sm)] hover:bg-[var(--color-surface-2)]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-6">
          {locked && (
            <p className="text-sm text-[var(--color-text-tertiary)] bg-[var(--color-surface-2)] px-4 py-3 rounded-[var(--radius-md)]">
              Questions are locked — attempts already exist for this round.
            </p>
          )}

          {questions.map((q, qIdx) => (
            <div key={qIdx} className="border border-[var(--color-border)] rounded-[var(--radius-lg)] p-4 space-y-3">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-[var(--color-accent)]">
                    {q.kind === 'survey' ? 'Survey question' : `Question ${qIdx + 1}`}
                  </span>
                  {q.kind === 'survey' && (
                    <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-[var(--color-accent-subtle)] text-[var(--color-accent)]">
                      Post-submit
                    </span>
                  )}
                </div>
                {!locked && questions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => onRemoveQuestion(qIdx)}
                    className="p-1.5 rounded hover:bg-[var(--color-error-subtle)] text-[var(--color-text-tertiary)] hover:text-[var(--color-error)]"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-[var(--color-text-secondary)]">Type</label>
                <select
                  className="border border-[var(--color-border)] rounded-[var(--radius-sm)] py-1.5 px-2 text-sm"
                  value={q.kind}
                  disabled={locked}
                  onChange={(ev) => onSetQuestionKind(qIdx, ev.target.value as QuizQuestionKind)}
                >
                  <option value="scored">Scored question</option>
                  <option value="survey">Survey question</option>
                </select>
                {q.kind === 'survey' && (
                  <p className="text-xs text-[var(--color-text-tertiary)]">
                    Shown only after the scored round is auto-submitted.
                  </p>
                )}
              </div>
              <textarea
                className="w-full border border-[var(--color-border)] rounded-[var(--radius-md)] py-3 px-4 text-sm min-h-[80px] resize-y"
                value={q.question}
                disabled={locked}
                onChange={(ev) => onUpdateQuestion(qIdx, 'question', ev.target.value)}
                placeholder="Enter the question text"
              />
              <div className="space-y-2">
                <p className="text-xs font-medium text-[var(--color-text-secondary)]">
                  {q.kind === 'survey' ? 'Survey options' : 'Answer options — select the correct one'}
                </p>
                {q.options.map((opt, oIdx) => (
                  <div key={oIdx} className="flex items-center gap-3">
                    {q.kind === 'scored' ? (
                      <input
                        type="radio"
                        name={`correct_${qIdx}`}
                        checked={q.correctAnswer === oIdx}
                        disabled={locked}
                        onChange={() => onUpdateQuestion(qIdx, 'correctAnswer', oIdx)}
                        className="accent-[var(--color-accent)]"
                      />
                    ) : (
                      <span className="w-4 h-4 rounded-full border border-[var(--color-border)] bg-[var(--color-surface-2)]" />
                    )}
                    <span className="w-6 text-xs font-medium text-[var(--color-text-tertiary)]">{String.fromCharCode(65 + oIdx)}</span>
                    <input
                      className="flex-1 border border-[var(--color-border)] rounded-[var(--radius-md)] py-2.5 px-3 text-sm"
                      value={opt}
                      disabled={locked}
                      onChange={(ev) => onUpdateOption(qIdx, oIdx, ev.target.value)}
                      placeholder={`Option ${String.fromCharCode(65 + oIdx)}`}
                    />
                  </div>
                ))}
              </div>
              {q.kind === 'scored' && (
                <div className="flex items-center gap-2">
                  <label className="text-xs text-[var(--color-text-secondary)]">Points</label>
                  <input
                    type="number"
                    min={1}
                    className="w-20 border border-[var(--color-border)] rounded-[var(--radius-sm)] py-1.5 px-2 text-sm"
                    value={q.points}
                    disabled={locked}
                    onChange={(ev) => onUpdateQuestion(qIdx, 'points', Number(ev.target.value))}
                  />
                </div>
              )}
              <QuestionMediaFields
                media={q.media}
                disabled={locked}
                onChange={(media) => onUpdateQuestion(qIdx, 'media', media)}
              />
            </div>
          ))}

          {!locked && (
            <button
              type="button"
              onClick={onAddQuestion}
              className="w-full py-3 border border-dashed border-[var(--color-border)] rounded-[var(--radius-md)] text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-2)]"
            >
              + Add question
            </button>
          )}
        </div>

        {!locked && (
          <div className="px-6 py-4 border-t border-[var(--color-border)] flex items-center justify-between gap-4 shrink-0">
            <div className="min-h-[1.25rem]">
              {saveState === 'saved' && (
                <span className="inline-flex items-center gap-1.5 text-sm text-[var(--color-success)]">
                  <Check className="w-4 h-4" /> Saved
                </span>
              )}
              {saveState === 'error' && saveError && (
                <span className="text-sm text-[var(--color-error)]">{saveError}</span>
              )}
            </div>
            <button
              type="button"
              disabled={saveState === 'saving'}
              onClick={onSave}
              className={cn(
                'text-sm font-medium px-5 py-2.5 rounded-[var(--radius-md)] transition-colors',
                saveState === 'saved'
                  ? 'bg-[var(--color-success-subtle)] text-[var(--color-success)]'
                  : 'bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)]',
                saveState === 'saving' && 'opacity-60 cursor-not-allowed',
              )}
            >
              {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved' : 'Save questions'}
            </button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

function QuestionMediaFields({
  media,
  disabled,
  onChange,
}: {
  media: QuizQuestionMedia | null;
  disabled: boolean;
  onChange: (media: QuizQuestionMedia | null) => void;
}) {
  const kind = media?.kind ?? '';
  const source = media?.source ?? 'url';
  const value = media?.value ?? '';

  const setKind = (nextKind: QuizQuestionMediaKind | '') => {
    if (!nextKind) {
      onChange(null);
      return;
    }
    onChange({
      kind: nextKind,
      source: media?.source ?? 'url',
      value: media?.value ?? '',
    });
  };

  const setSource = (nextSource: QuizQuestionMediaSource) => {
    if (!media?.kind) return;
    onChange({ ...media, source: nextSource });
  };

  const setValue = (nextValue: string) => {
    if (!media?.kind) return;
    onChange({ ...media, value: nextValue });
  };

  const selectClassName =
    'w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-2.5 px-3 text-sm';

  return (
    <div className="space-y-3 pt-2 border-t border-[var(--color-border)]">
      <p className="text-xs font-medium text-[var(--color-text-secondary)]">Media (optional)</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-[var(--color-text-tertiary)] mb-1">Type</label>
          <select
            className={selectClassName}
            value={kind}
            disabled={disabled}
            onChange={(ev) => setKind(ev.target.value as QuizQuestionMediaKind | '')}
          >
            <option value="">None</option>
            <option value="image">Image</option>
            <option value="audio">Audio</option>
            <option value="video">Video</option>
          </select>
        </div>
        {kind && (
          <div>
            <label className="block text-xs text-[var(--color-text-tertiary)] mb-1">Source</label>
            <select
              className={selectClassName}
              value={source}
              disabled={disabled}
              onChange={(ev) => setSource(ev.target.value as QuizQuestionMediaSource)}
            >
              <option value="url">External URL</option>
              <option value="static">Static asset</option>
            </select>
          </div>
        )}
      </div>
      {kind && (
        <div>
          <label className="block text-xs text-[var(--color-text-tertiary)] mb-1">
            {source === 'static' ? 'Asset filename' : 'Media URL'}
          </label>
          <input
            className="w-full border border-[var(--color-border)] rounded-[var(--radius-md)] py-2.5 px-3 text-sm"
            value={value}
            disabled={disabled}
            onChange={(ev) => setValue(ev.target.value)}
            placeholder={
              source === 'static'
                ? 'intro-bell.mp3'
                : 'https://example.com/media.jpg'
            }
          />
          {source === 'static' && (
            <p className="text-[11px] text-[var(--color-text-tertiary)] mt-1">
              Place files in <code className="font-mono">public/assets/quiz/</code> — served at{' '}
              <code className="font-mono">{QUIZ_STATIC_ASSET_PREFIX}{'{name}'}</code>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function SurveyResultsModal({
  sessionLabel,
  results,
  onClose,
}: {
  sessionLabel: string;
  results: QuizSurveyResults;
  onClose: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
    >
      <button type="button" className="absolute inset-0 bg-black/40" aria-label="Close" onClick={onClose} />
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        className="relative w-full sm:max-w-3xl max-h-[90vh] overflow-hidden bg-[var(--color-surface-1)] rounded-t-[var(--radius-xl)] sm:rounded-[var(--radius-xl)] border border-[var(--color-border)] shadow-[var(--shadow-lg)] flex flex-col"
      >
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-[var(--color-border)] shrink-0">
          <div>
            <h3 className="text-base font-semibold text-[var(--color-text-primary)]">Survey Results</h3>
            <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">{sessionLabel}</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-[var(--radius-sm)] hover:bg-[var(--color-surface-2)]">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-6">
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wide text-[var(--color-text-tertiary)]">Survey question</p>
            <h4 className="text-base font-semibold text-[var(--color-text-primary)]">{results.question.question}</h4>
            <p className="text-sm text-[var(--color-text-secondary)]">{results.totalResponses} response{results.totalResponses === 1 ? '' : 's'}</p>
          </div>

          <div className="space-y-3">
            {results.options.map((option) => (
              <div key={option.index} className="space-y-1">
                <div className="flex items-center justify-between gap-4 text-sm">
                  <span className="text-[var(--color-text-primary)]">{option.label}</span>
                  <span className="text-[var(--color-text-secondary)]">{option.count}</span>
                </div>
                <div className="w-full h-2 rounded-full bg-[var(--color-surface-2)] overflow-hidden">
                  <div
                    className="h-full bg-[var(--color-accent)] rounded-full"
                    style={{
                      width: `${results.totalResponses === 0 ? 0 : (option.count / results.totalResponses) * 100}%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-3">
            <h5 className="text-sm font-semibold text-[var(--color-text-primary)]">Responses</h5>
            {results.responses.length === 0 ? (
              <p className="text-sm text-[var(--color-text-secondary)]">No responses yet.</p>
            ) : (
              <div className="space-y-2">
                {results.responses.map((response) => (
                  <div
                    key={`${response.uid}-${response.submittedAt}`}
                    className="border border-[var(--color-border)] rounded-[var(--radius-md)] px-3 py-2.5 text-sm flex items-center justify-between gap-3"
                  >
                    <div>
                      <p className="font-medium text-[var(--color-text-primary)]">{response.memberName}</p>
                      <p className="text-xs text-[var(--color-text-secondary)]">{response.selectedLabel}</p>
                    </div>
                    <p className="text-xs text-[var(--color-text-tertiary)]">{new Date(response.submittedAt).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function StatCard({ label, value, meta }: { label: string; value: string; meta: string }) {
  return (
    <div className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-1)] px-4 py-3">
      <p className="text-xs text-[var(--color-text-tertiary)]">{label}</p>
      <p className="mt-1 text-xl font-semibold text-[var(--color-text-primary)]">{value}</p>
      <p className="mt-1 text-xs text-[var(--color-text-secondary)]">{meta}</p>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  required,
  textarea,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  textarea?: boolean;
  hint?: React.ReactNode;
}) {
  const className =
    'w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-3 px-4 text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent)] text-sm';
  return (
    <div>
      <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5">{label}</label>
      {textarea ? (
        <textarea className={className} rows={2} value={value} onChange={(ev) => onChange(ev.target.value)} required={required} />
      ) : (
        <input className={className} type={type} value={value} onChange={(ev) => onChange(ev.target.value)} placeholder={placeholder} required={required} />
      )}
      {hint && <p className="text-[11px] text-[var(--color-text-tertiary)] mt-1">{hint}</p>}
    </div>
  );
}
